import { useEffect, useRef, useState } from 'react'

import { buildWsUrl } from '@/config/env'
import { tokenStorage } from '@/lib/storage/tokenStorage'

interface WebSocketOptions<TParsed> {
  enabled?: boolean
  parse?: (event: MessageEvent) => TParsed
  onMessage?: (data: TParsed) => void
  onOpen?: (event: Event) => void
  onClose?: (event: CloseEvent) => void
  onError?: (event: Event) => void
  protocols?: string | string[]
  reconnectIntervalMs?: number
}

export const useWebSocket = <TParsed = unknown>(
  path: string,
  {
    enabled = true,
    parse,
    onMessage,
    onOpen,
    onClose,
    onError,
    protocols,
    reconnectIntervalMs = 4000,
  }: WebSocketOptions<TParsed> = {},
) => {
  const [isConnected, setIsConnected] = useState(false)
  const [lastMessage, setLastMessage] = useState<TParsed | null>(null)
  const socketRef = useRef<WebSocket | null>(null)
  const reconnectTimeoutRef = useRef<number | null>(null)
  const shouldReconnectRef = useRef(true)
  const isConnectingRef = useRef(false)

  // Use refs for callbacks to avoid dependency issues
  const onMessageRef = useRef(onMessage)
  const onOpenRef = useRef(onOpen)
  const onCloseRef = useRef(onClose)
  const onErrorRef = useRef(onError)
  const parseRef = useRef(parse)

  useEffect(() => {
    onMessageRef.current = onMessage
    onOpenRef.current = onOpen
    onCloseRef.current = onClose
    onErrorRef.current = onError
    parseRef.current = parse
  }, [onMessage, onOpen, onClose, onError, parse])

  useEffect(() => {
    if (!enabled) {
      return
    }

    shouldReconnectRef.current = true

    const connect = () => {
      // Prevent multiple simultaneous connection attempts
      if (isConnectingRef.current) {
        return
      }

      // Close existing connection if any
      if (socketRef.current) {
        if (socketRef.current.readyState === WebSocket.OPEN ||
          socketRef.current.readyState === WebSocket.CONNECTING) {
          socketRef.current.close()
        }
        socketRef.current = null
      }

      isConnectingRef.current = true

      const accessToken = tokenStorage.getAccessToken()
      const wsUrl = buildWsUrl(path)

      // Extract userId from JWT token for admin WebSocket
      let userId = 'admin'
      if (accessToken) {
        try {
          const payload = JSON.parse(atob(accessToken.split('.')[1]))
          userId = payload.sub || payload.id || 'admin'
        } catch (e) {
          console.warn('Failed to parse token for userId:', e)
        }
      }

      const urlWithParams = `${wsUrl}?userId=${encodeURIComponent(userId)}`
      console.log('Connecting to WebSocket:', urlWithParams)

      try {
        const socket = new WebSocket(urlWithParams, protocols)
        socketRef.current = socket

        socket.addEventListener('open', (event) => {
          console.log('WebSocket opened successfully')
          isConnectingRef.current = false
          setIsConnected(true)
          if (onOpenRef.current) {
            onOpenRef.current(event)
          }
        })

        socket.addEventListener('message', (event) => {
          let parsed: TParsed | null = null

          if (parseRef.current) {
            parsed = parseRef.current(event)
          } else {
            try {
              parsed = JSON.parse(event.data)
            } catch {
              parsed = (event.data as TParsed) ?? null
            }
          }

          if (parsed) {
            setLastMessage(parsed)
            onMessageRef.current?.(parsed)
          }
        })

        socket.addEventListener('close', (event) => {
          console.log('WebSocket closed:', event.code, event.reason)
          isConnectingRef.current = false
          setIsConnected(false)
          socketRef.current = null

          if (onCloseRef.current) {
            onCloseRef.current(event)
          }

          // Only reconnect if we should and the close wasn't clean
          if (shouldReconnectRef.current && event.code !== 1000) {
            console.log(`Reconnecting in ${reconnectIntervalMs}ms...`)
            reconnectTimeoutRef.current = window.setTimeout(connect, reconnectIntervalMs)
          }
        })

        socket.addEventListener('error', (event) => {
          console.error('WebSocket error:', event)
          isConnectingRef.current = false
          if (onErrorRef.current) {
            onErrorRef.current(event)
          }
        })
      } catch (error) {
        console.error('Failed to create WebSocket:', error)
        isConnectingRef.current = false
      }
    }

    connect()

    return () => {
      console.log('Cleaning up WebSocket connection')
      shouldReconnectRef.current = false
      isConnectingRef.current = false

      if (reconnectTimeoutRef.current) {
        window.clearTimeout(reconnectTimeoutRef.current)
        reconnectTimeoutRef.current = null
      }

      if (socketRef.current) {
        socketRef.current.close(1000, 'Component unmounting')
        socketRef.current = null
      }
    }
  }, [enabled, path, reconnectIntervalMs, protocols])

  const send = (payload: string | Record<string, unknown>) => {
    const socket = socketRef.current
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      console.warn('Cannot send message: WebSocket is not connected')
      return false
    }

    try {
      const data = typeof payload === 'string' ? payload : JSON.stringify(payload)
      socket.send(data)
      return true
    } catch (error) {
      console.error('Failed to send WebSocket message:', error)
      return false
    }
  }

  return {
    isConnected,
    lastMessage,
    send,
  }
}
